import { Injectable } from '@angular/core';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { COPILOT_MSAL_REDIRECT_PATH } from '../models/copilot/copilot-consent.enum';

/**
 * Parent-app (Frankly) Microsoft sign-in for M365 Copilot. Acquires an access token for our API
 * scope (api://<clientId>/Copilot.Invoke); ChatWindowComponent posts it into the IntelliCatch chat
 * iframe, which forwards it to IntelliCatchService for the On-Behalf-Of exchange that calls Copilot.
 *
 * tryGetTokenSilent() is safe to call on load (no popup); signInAndGetApiToken() needs a user gesture.
 * Every method is a no-op until environment.copilotMsal.appId + apiScope are configured.
 */
@Injectable({ providedIn: 'root' })
export class CopilotAuthService {
  private pca?: PublicClientApplication;

  isConfigured(): boolean {
    const c = environment.copilotMsal;
    return !!c && !!c.appId && !!c.apiScope;
  }

  /**
   * The reply URL for this deployment. Derived from the running origin rather than configured, so
   * one bundle serves every environment and the value can never drift from where the app is served.
   * Each origin still has to be registered as a SPA redirect URI on the app registration.
   */
  private redirectUri(): string {
    return `${window.location.origin}${COPILOT_MSAL_REDIRECT_PATH}`;
  }

  /** Remove any leftover MSAL "interaction in progress" markers from a previously blocked popup. */
  private clearStuckInteraction(): void {
    for (const store of [localStorage, sessionStorage]) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && key.indexOf('interaction.status') !== -1) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => store.removeItem(k));
    }
  }

  private async getPca(): Promise<PublicClientApplication> {
    if (!this.pca) {
      this.pca = new PublicClientApplication({
        auth: {
          clientId: environment.copilotMsal.appId,
          authority: environment.copilotMsal.authority,
          redirectUri: this.redirectUri(),
          // Without this MSAL sends the logout popup to the current page, which loads the whole
          // Frankly app inside the popup - the user sees a login screen in a small window, and
          // because the popup never reaches a URI MSAL recognises, logoutPopup() never settles.
          // The sign-in bridge page is blank and already registered, so it costs no Entra change.
          postLogoutRedirectUri: this.redirectUri(),
        },
        cache: { cacheLocation: 'localStorage' },
        system: {
          // msal-browser waits this long for the redirect bridge to broadcast the popup's response
          // and then rejects with "timed_out". The 60s default is shorter than a real Microsoft
          // sign-in that involves MFA, so the wait would expire while the user was still approving
          // on their phone - and because the opener has stopped listening by then, the response
          // that arrives afterwards is dropped and the completed sign-in appears to do nothing.
          // Kept well short of the old 5 minutes: a popup closed before it reaches the bridge has
          // nothing to broadcast, so this wait is also how long a cancelled sign-in hangs for.
          // ChatWindowComponent runs its own watchdog so the UI never waits even this long.
          popupBridgeTimeout: 120000,
        },
      });
      await this.pca.initialize();
    }
    return this.pca;
  }

  /**
   * Return a token from an existing MSAL session WITHOUT showing a popup, or null if there is no
   * cached account/token. Safe to call outside a user gesture (popups are never opened here).
   */
  async tryGetTokenSilent(): Promise<string | null> {
    if (!this.isConfigured()) {
      return null;
    }
    const scopes = [environment.copilotMsal.apiScope];
    const pca = await this.getPca();
    const account: AccountInfo | null = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
    if (!account) {
      return null;
    }
    try {
      const result = await pca.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch (err) {
      // Usually "interaction required" for an expired session - expected. Log so a genuine MSAL/config
      // fault (bad scope, misconfigured app registration) is not hidden behind the "not signed in" path.
      console.warn('Copilot tryGetTokenSilent failed', err);
      return null;
    }
  }

  /** Sign in (if needed) and return an access token for our API scope, or null if not configured. */
  async signInAndGetApiToken(): Promise<string | null> {
    if (!this.isConfigured()) {
      return null;
    }
    const scopes = [environment.copilotMsal.apiScope];
    const pca = await this.getPca();
    // A popup that was blocked or interrupted leaves MSAL's interaction flag stuck, which makes every
    // later loginPopup reject with "interaction_in_progress". Clear it before starting a fresh sign-in.
    this.clearStuckInteraction();
    let account: AccountInfo | null = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
    if (!account) {
      account = await this.loginWithRecovery(pca, scopes);
      if (!account) {
        return null;
      }
      pca.setActiveAccount(account);
    }
    try {
      const result = await pca.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch {
      const result = await pca.acquireTokenPopup({ scopes });
      return result.accessToken;
    }
  }

  /**
   * End the Microsoft session, not just this app's copy of it. logoutPopup keeps the user on the
   * page, unlike a redirect logout which would navigate Frankly away and lose unsaved work.
   */
  async signOut(): Promise<void> {
    if (!this.isConfigured()) {
      return;
    }
    const pca = await this.getPca();
    const account = this.cachedAccount(pca);

    this.clearStuckInteraction();
    try {
      await pca.logoutPopup({ account: account ?? undefined });
    } catch (err) {
      // A blocked or closed popup must still leave the app signed out locally, or the user is
      // stuck on an account they have asked to leave.
      console.warn('Copilot signOut popup did not complete; clearing locally', err);
      await this.forgetLocalAccounts(pca);
    }
  }

  /**
   * Drop every cached account and the active-account pointer that survives them. msal-browser v5
   * replaced the per-account token cache with clearCache(); setActiveAccount(null) still has to be
   * explicit, because the pointer outlives the cache entry it referred to.
   */
  private async forgetLocalAccounts(pca: PublicClientApplication): Promise<void> {
    if (!pca) {
      return;
    }
    try {
      await pca.clearCache();
    } catch (err) {
      console.warn('Copilot: could not clear the MSAL cache', err);
    }
    pca.setActiveAccount(null);
  }

  /**
   * Open the sign-in popup, recovering from the two ways it fails without the user having done
   * anything wrong: a wait that expired while they were still authenticating, and a stale
   * interaction flag left behind by an earlier popup that was blocked or abandoned. Either can
   * otherwise leave the user stuck with a sign-in that never completes until browser storage is
   * cleared by hand.
   *
   * Null when no account can be established. msal-browser narrows the popup result's account to
   * non-null, but msal-common declares it nullable, so the compiler cannot rule out a sign-in that
   * resolves without one - fall back to whatever MSAL has cached before giving up.
   */
  private async loginWithRecovery(pca: PublicClientApplication, scopes: string[]): Promise<AccountInfo | null> {
    if (!pca || !scopes?.length) {
      return null;
    }
    try {
      const login = await pca.loginPopup({ scopes });
      return login?.account ?? this.cachedAccount(pca);
    } catch (err) {
      // The sign-in may have completed after the wait expired, in which case the account is
      // already cached and asking for another popup would be pointless.
      const cached = this.cachedAccount(pca);
      if (cached) {
        return cached;
      }
      if (!this.isRecoverableSignInError(err)) {
        throw err;
      }
      // Drop the stale interaction marker and try once more, so a single abandoned popup does not
      // block every later attempt.
      this.clearStuckInteraction();
      const retry = await pca.loginPopup({ scopes });
      return retry?.account ?? this.cachedAccount(pca);
    }
  }

  private cachedAccount(pca: PublicClientApplication): AccountInfo | null {
    return pca?.getActiveAccount() ?? pca?.getAllAccounts()?.[0] ?? null;
  }

  /**
   * The Microsoft account's own sign-in name (UPN/email) for the account currently signed in to
   * Copilot, or null if no popup has completed yet in this session. This can differ from the
   * Frankly user's own login - the two systems have separate accounts - so it is worth recording
   * alongside the Frankly user rather than assumed to match.
   */
  getSignedInUsername(): string | null {
    return this.pca ? this.cachedAccount(this.pca)?.username ?? null : null;
  }

  private isRecoverableSignInError(err: unknown): boolean {
    const code = (err as { errorCode?: string })?.errorCode ?? '';
    return code === 'timed_out' || code === 'interaction_in_progress';
  }
}
